<template>
    <MultiviewEditableWidget
        class="editable-folder-widget"
        :is-opened="isOpen"
        :read-only="readOnly"
        @input="toggleOpen"
    >
        <template #name>{{ editable.label }}</template>
        <template #controls-prefix>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>
        <template #controls></template>
        <template #list-view>
            <EditableFileListView
                :editable="editable"
                :groups-affecting-editable="groupsAffectingEditable"
                :is-opened="isOpen"
                :type="editable.type"
                @update="updateFileEditable"
            />
        </template>
        <template #grid-view>
            <EditableFileGridView
                :editable="editable"
                :groups-affecting-editable="groupsAffectingEditable"
                :is-opened="isOpen"
                :type="editable.type"
                @update="updateFileEditable"
            />
        </template>
    </MultiviewEditableWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, ref, type PropType } from "vue";
import type { Editable } from "hoxton-types";

import EditableFileGridView from "@/components/Campaign/Editables/EditableFileGridView.vue";
import EditableFileListView from "@/components/Campaign/Editables/EditableFileListView.vue";
import MultiviewEditableWidget from "@/components/Campaign/widgets/MultiviewEditableWidget.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";
import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useEditableFile } from "@/composables/useEditableFile";

export default defineComponent({
    name: "EditableFolderWidget",

    components: {
        EditableRestrictionControl,
        MultiviewEditableWidget,
        EditableFileListView,
        EditableFileGridView
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");
        const groupsAffectingEditable = toRef(props, "groupsAffectingEditable");
        const type = ref(editable.value.type);

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { updateFileEditable } = useEditableFile({ emit, editable, groupsAffectingEditable, type });

        return {
            isOpen,
            toggleOpen,
            updateFileEditable
        };
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.editable-folder-item .widget-mode {
    color: $cmp-dark-disabled-color;
}

.editable-folder-item .widget-mode--active {
    color: $cmp-dark-font-color;
}
</style>
